import React from "react";
import rehypeReact from "rehype-react";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";
import { nl2br, HyperLink, StyledImg } from "~/molecules/BaseBody";

type Node = {
  children?: React.ReactNode;
};

export const InsightBlockquote: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .blockquote {
          color: ${colors.defaultText};
          border: 1px solid ${colors.gray4};
          border-radius: 12px;
          margin: 24px 0;
          padding: 20px 24px;
          font-size: 16px;
        }
        .blockquote :global(p) {
          margin: 0;
        }
        @media (${breakPoints.sp}) {
          .blockquote {
            font-size: 14px;
            padding: 16px 20px;
          }
        }
      `}</style>
      <blockquote className="blockquote">{node.children}</blockquote>
    </>
  );
};

export const Paragraph: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .paragraph {
          color: ${colors.defaultText};
          margin: 24px 0 0;
          font-size: 16px;
          line-height: 2;
          font-weight: 400;
        }
        .paragraph:first-child {
          margin: 0;
        }
        .paragraph :global(strong) {
          font-weight: 700;
        }
        @media (${breakPoints.sp}) {
          .paragraph {
            font-size: 14px;
          }
        }
      `}</style>
      <p className="paragraph">{node.children}</p>
    </>
  );
};

export const Heading1: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .heading1 {
          color: ${colors.defaultText};
          margin: 48px 0 16px;
          font-weight: 700;
        }
        @media (${breakPoints.sp}) {
          .heading1 {
            font-size: 2.2rem;
          }
        }
        @media (${breakPoints.minPc}) {
          .heading1 {
            font-size: 2.8rem;
          }
        }
      `}</style>
      <h1 className="heading1">{node.children}</h1>
    </>
  );
};

export const Heading2: React.FC<Node> = (node) => {
  const headingText =
    typeof node.children === "string"
      ? node.children
      : Array.isArray(node.children) && node.children[0]?.value
      ? node.children[0].value
      : String(node.children || "");

  return (
    <>
      <style jsx>{`
        .heading2 {
          color: ${colors.defaultText};
          margin: 56px 0 24px 0;
          padding: 0 0 10px;
          border-bottom: solid 2px ${colors.gray3};
          scroll-margin-top: 140px;
        }
        @media (${breakPoints.sp}) {
          .heading2 {
            font-size: 2.2rem;
            scroll-margin-top: 140px;
          }
        }
        @media (${breakPoints.minPc}) {
          .heading2 {
            font-size: 2.8rem;
            scroll-margin-top: 140px;
          }
        }
      `}</style>
      {headingText && (
        <h2 id={headingText} className="heading2">
          {headingText}
        </h2>
      )}
    </>
  );
};

export const Heading3: React.FC<Node> = (node) => {
  const headingText =
    typeof node.children === "string"
      ? node.children
      : Array.isArray(node.children) && node.children[0]?.value
      ? node.children[0].value
      : String(node.children || "");

  return (
    <>
      <style jsx>{`
        .heading3 {
          color: ${colors.defaultText};
          margin: 40px 0 16px 0;
          font-weight: 700;
          padding-left: 20px;
          position: relative;
          scroll-margin-top: 140px;
        }
        .heading3::before {
          content: "";
          background-color: #ff6400;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 4px;
          border-radius: 2px;
        }
        @media (${breakPoints.sp}) {
          .heading3 {
            font-size: 1.8rem;
            scroll-margin-top: 140px;
          }
        }
        @media (${breakPoints.minPc}) {
          .heading3 {
            font-size: 2rem;
            padding-left: 20px;
            scroll-margin-top: 140px;
          }
        }
      `}</style>
      {headingText && (
        <h3 id={headingText} className="heading3">
          {node.children}
        </h3>
      )}
    </>
  );
};

export const Heading4: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .heading4 {
          color: ${colors.defaultText};
          margin: 32px 0 12px 0;
          padding: 0;
          font-weight: 700;
        }
        .heading4:first-child {
          margin: 0;
        }
        @media (${breakPoints.sp}) {
          .heading4 {
            font-size: 1.6rem;
          }
        }
        @media (${breakPoints.minPc}) {
          .heading4 {
            font-size: 1.8rem;
          }
        }
      `}</style>
      <h4 className="heading4">{node.children}</h4>
    </>
  );
};

export const StyledUl: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledUl {
          color: ${colors.defaultText};
          margin: 24px 0;
          padding: 20px 24px 20px 48px;
          list-style: disc;
          font-size: 16px;
          font-weight: 400;
          border: 1px solid ${colors.gray4};
          border-radius: 12px;
        }
        .styledUl :global(li)::marker {
          color: ${colors.orange};
        }
        .styledUl :global(li) {
          margin-top: 10px;
        }
        @media (${breakPoints.sp}) {
          .styledUl {
            font-size: 14px;
          }
        }
      `}</style>
      <ul className="styledUl">{node.children}</ul>
    </>
  );
};

export const StyledOl: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledOl {
          color: ${colors.defaultText};
          margin: 24px 0;
          padding: 20px 24px 20px 48px;
          list-style: number;
          font-size: 16px;
          font-weight: 400;
          border: 1px solid ${colors.gray4};
          border-radius: 12px;
        }
        .styledOl :global(li)::marker {
          color: ${colors.orange};
          font-weight: 700;
        }
        .styledOl :global(li) {
          margin-top: 10px;
        }
        @media (${breakPoints.sp}) {
          .styledOl {
            font-size: 14px;
          }
        }
      `}</style>
      <ol className="styledOl">{node.children}</ol>
    </>
  );
};

export const StyledHr: React.FC<Node> = () => {
  return (
    <>
      <style jsx>{`
        .styledHr {
          border: none;
          border-top: 1px solid ${colors.gray4};
          height: 0;
          margin: 24px 0;
        }
        @media (${breakPoints.sp}) {
          .styledHr {
            margin: 20px 0;
          }
        }
      `}</style>
      <hr className="styledHr" />
    </>
  );
};

export const StyledTable: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledTableWrapper {
          margin: 32px 0;
          overflow-x: auto;
          border: 1px solid ${colors.gray4};
          border-radius: 12px;
        }
        .styledTable {
          width: 100%;
          border-collapse: collapse;
          margin: 0;
          font-size: 1.6rem;
        }
        .styledTableWrapper :global(tr th:first-child),
        .styledTableWrapper :global(tr td:first-child) {
          border-left: none;
        }
        .styledTableWrapper :global(tr th:last-child),
        .styledTableWrapper :global(tr td:last-child) {
          border-right: none;
        }
        .styledTableWrapper :global(thead tr:first-child th),
        .styledTableWrapper :global(thead tr:first-child td) {
          border-top: none;
        }
        .styledTableWrapper :global(tbody tr:last-child th),
        .styledTableWrapper :global(tbody tr:last-child td) {
          border-bottom: none;
        }
        @media (${breakPoints.sp}) {
          .styledTable {
            font-size: 14px;
          }
        }
      `}</style>
      <div className="styledTableWrapper">
        <table className="styledTable">{node.children}</table>
      </div>
    </>
  );
};

export const StyledThead: React.FC<Node> = (node) => {
  return <thead>{node.children}</thead>;
};

export const StyledTbody: React.FC<Node> = (node) => {
  return <tbody>{node.children}</tbody>;
};

export const StyledTr: React.FC<Node> = (node) => {
  return <tr>{node.children}</tr>;
};

export const StyledTh: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledTh {
          border: 1px solid ${colors.gray4};
          padding: 12px 16px;
          text-align: left;
          background-color: ${colors.gray};
          font-weight: 700;
          font-size: 1.4rem;
        }
        @media (${breakPoints.sp}) {
          .styledTh {
            padding: 10px 12px;
            font-size: 12px;
          }
        }
      `}</style>
      <th className="styledTh">{node.children}</th>
    </>
  );
};

export const StyledTd: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledTd {
          border: 1px solid ${colors.gray4};
          padding: 12px 16px;
          text-align: left;
        }
        @media (${breakPoints.sp}) {
          .styledTd {
            padding: 10px 12px;
            font-size: 14px;
          }
        }
      `}</style>
      <td className="styledTd">{node.children}</td>
    </>
  );
};

export const renderAst = new rehypeReact({
  createElement: React.createElement,
  components: {
    h1: Heading1,
    h2: Heading2,
    h3: Heading3,
    h4: Heading4,
    h5: Heading4,
    h6: Heading4,
    p: Paragraph,
    a: HyperLink,
    ul: StyledUl,
    ol: StyledOl,
    blockquote: InsightBlockquote,
    hr: StyledHr,
    img: StyledImg,
    table: StyledTable,
    thead: StyledThead,
    tbody: StyledTbody,
    tr: StyledTr,
    th: StyledTh,
    td: StyledTd,
  },
}).Compiler;

export const renderHtmlAst = (htmlAst) => {
  return renderAst(nl2br(htmlAst));
};
